import { useEffect } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { observer } from 'mobx-react';
import { VideoView } from 'expo-video';
import Ionicons from '@expo/vector-icons/Ionicons';

import { ThemedText } from '@/components/ThemedText';
import { useScale } from '@/hooks/useScale';
import GuidedProgressBar from '@/components/GuidedProgressBar';
import { IGuidedWorkoutPlayerStore } from '@/stores/guidedWorkoutPlayer/GuidedWorkoutPlayerStore';
import { useGuidedPlayerController } from '@/hooks/useGuidedPlayerController';
import { useTVTransportControls } from '@/hooks/useTVTransportControls';

type Props = {
  store: IGuidedWorkoutPlayerStore;
  autoFocusPause: boolean;
  onBack: () => void;
};

// Background of the right-side info panel. 'transparent' reveals the shared
// <DefaultBackground /> gradient.
const INFO_BACKGROUND_COLOR = 'transparent';

// The Back button is only shown on web — TV navigates back with the remote's
// menu/back button, so it doesn't need an on-screen control.
const isWeb = Platform.OS === 'web';

// Width of the (right-aligned) video column as a % of screen width. The info
// panel takes the rest.
const VIDEO_COLUMN_PERCENT = 38;

// Video aspect ratio (portrait 9:16) and the vertical inset that keeps it off
// the top/bottom edges. Shared between the fit math and the column padding.
const VIDEO_ASPECT = 9 / 16;
const VIDEO_VERTICAL_INSET = 28;

// Minimum breathing room on the left of the video. The video is right-anchored
// against the info panel, so reserving this from its max width leaves the gap
// on the left (instead of sitting flush to the screen edge) on narrow windows.
const VIDEO_LEFT_INSET = 24;

// Title line height (px before scaling). The title box reserves two lines of
// this height and bottom-anchors the text, so switching between a 1- and 2-line
// title doesn't shift the time/progress/controls below it.
const TITLE_LINE_HEIGHT = 50;

/**
 * Guided workout player — a rounded, inset video on the left and a centered
 * info panel (chapter title, time, progress bar, controls) on the right.
 * Owns its own Back overlay so the video column can use the full screen height.
 */
const GuidedWorkoutPlayer = observer(
  ({ store, autoFocusPause, onBack }: Props) => {
    const scale = useScale();
    const styles = useStyles();
    const { player } = useGuidedPlayerController(store);

    // Size the video to fit inside BOTH the column width and the available
    // height (contain), so narrowing the window shrinks it instead of clipping
    // the left edge. On tall/wide screens (e.g. 1024x720, TVs) height is the
    // binding constraint, so this matches the previous flex + aspectRatio look.
    const { width, height } = useWindowDimensions();
    const columnWidth = width * (VIDEO_COLUMN_PERCENT / 100);
    // Reserve a little space on the left so the video keeps some breathing room
    // from the screen edge when width is the binding constraint (narrow window).
    const videoMaxWidth = Math.max(0, columnWidth - VIDEO_LEFT_INSET * scale);
    const availableHeight = Math.max(0, height - VIDEO_VERTICAL_INSET * scale * 2);
    const videoHeight = Math.min(availableHeight, videoMaxWidth / VIDEO_ASPECT);
    const videoWidth = videoHeight * VIDEO_ASPECT;

    // Hardware transport buttons mirror the on-screen controls below.
    useTVTransportControls({
      onRewind: () => store.restartCurrentChapter(),
      // On Android the expo-video MediaSession already toggles the player on the
      // hardware play/pause button, and we mirror that into the store via
      // `playingChange` (see useGuidedPlayerController) — so handling it here too
      // would double-toggle (cancel out). iOS has no such session, so we drive the
      // toggle ourselves.
      onPlayPause:
        Platform.OS === 'android' ? undefined : () => store.togglePause(),
      onFastForward: () => store.skipToNextChapter(),
    });

    // Listen for SW postMessage with fetch source info (debug only)
    useEffect(() => {
      if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;

      const handler = (event: MessageEvent) => {
        console.log('[GuidedUI] Received SW message:', event.data);
      };
      navigator.serviceWorker.addEventListener('message', handler);
      return () =>
        navigator.serviceWorker.removeEventListener('message', handler);
    }, []);

    return (
      <View style={styles.playerContainer}>
        {/* Back button — web only. On TV the remote's back/menu button handles it. */}
        {isWeb && (
          <Pressable
            style={({ focused, hovered }) => [
              styles.backButton,
              (focused || hovered) && styles.backButtonFocused,
            ]}
            onPress={onBack}
          >
            <ThemedText style={styles.backButtonText}>Back</ThemedText>
          </Pressable>
        )}

        {/* Left column: video, slightly inset vertically, right-aligned */}
        <View style={styles.videoColumn}>
          <View
            style={[
              styles.videoWrapper,
              { width: videoWidth, height: videoHeight },
            ]}
          >
            <VideoView
              player={player}
              style={styles.video}
              nativeControls={false}
              contentFit="contain"
            />
          </View>
        </View>

        {/* Right: chapter info + progress + controls, grouped and centered */}
        <View style={styles.infoPanel}>
          {/* Header: chapter title + "Chapter X/Y" */}
          <View style={styles.infoHeader}>
            <View style={styles.titleContainer}>
              <ThemedText style={styles.infoTitle}>
                {store.currentChapterTitle}
              </ThemedText>
            </View>
            <ThemedText style={styles.infoSubtitle}>
              {store.chapterProgressLabel}
            </ThemedText>
          </View>

          {/* Footer: time + progress bar + controls */}
          <View style={styles.infoFooter}>
            <View style={styles.progressSection}>
              <ThemedText style={styles.timeLabel}>
                {store.timeLabel}
              </ThemedText>
              <View style={styles.progressBarContainer}>
                <GuidedProgressBar
                  progress={store.overallProgress}
                  markers={store.chapterMarkers}
                />
              </View>
            </View>

            {/* Icon controls */}
            <View style={styles.controls}>
              <Pressable
                style={({ focused }) => [
                  styles.controlButton,
                  focused && styles.controlButtonFocused,
                ]}
                onPress={() => store.restartCurrentChapter()}
              >
                <Ionicons
                  name="refresh-outline"
                  style={{ transform: [{ scaleX: -1 }] }}
                  size={22 * scale}
                  color="#fff"
                />
              </Pressable>

              <Pressable
                // We use a combination of key and dynamic hasTVPreferredFocus to force a re-mount of the component
                // when we need it to be ensure the preferred focus behavior.
                // It seems that our countdown overlay invalidates the preferred focus on the first mount since
                // it covers everything. But if we force a re-mount after the countdown is done, the focus works as expected.
                key={autoFocusPause ? 'pause-focus' : 'pause-no-focus'}
                hasTVPreferredFocus={autoFocusPause}
                style={({ focused }) => [
                  styles.controlButton,
                  styles.controlButtonLarge,
                  focused && styles.controlButtonFocused,
                ]}
                onPress={() => store.togglePause()}
              >
                <Ionicons
                  name={store.isPaused ? 'play' : 'pause'}
                  size={28 * scale}
                  color="#fff"
                />
              </Pressable>

              <Pressable
                style={({ focused }) => [
                  styles.controlButton,
                  focused && styles.controlButtonFocused,
                ]}
                onPress={() => store.skipToNextChapter()}
              >
                <Ionicons
                  name={store.isLastChapter ? 'stop' : 'play-skip-forward'}
                  size={22 * scale}
                  color="#fff"
                />
              </Pressable>
            </View>
          </View>
        </View>
      </View>
    );
  },
);

export default GuidedWorkoutPlayer;

function useStyles() {
  const scale = useScale();

  return StyleSheet.create({
    playerContainer: {
      flex: 1,
      flexDirection: 'row',
    },
    videoColumn: {
      width: `${VIDEO_COLUMN_PERCENT}%`,
      height: '100%',
      // Push the video to the right edge of the left column, against the panel.
      alignItems: 'flex-end',
      justifyContent: 'center',
      // A small vertical inset — keeps the video tall but off the screen edges.
      paddingVertical: VIDEO_VERTICAL_INSET * scale,
    },
    videoWrapper: {
      // Width/height are computed per-render (contain fit); see the component.
      borderRadius: 28 * scale,
      overflow: 'hidden',
      backgroundColor: 'transparent',
    },
    video: {
      width: '100%',
      height: '100%',
    },
    infoPanel: {
      flex: 1,
      // Group the title + controls and center them vertically (no video sits in
      // the middle here, so there's no reason to pin them to the edges).
      justifyContent: 'center',
      gap: 44 * scale,
      paddingHorizontal: 56 * scale,
      paddingVertical: 32 * scale,
      backgroundColor: INFO_BACKGROUND_COLOR,
    },
    infoHeader: {
      gap: 8 * scale,
    },
    titleContainer: {
      // Reserve two lines of title height and bottom-anchor the text, so a 1- vs
      // 2-line title grows upward into reserved space instead of pushing the
      // footer (time / progress / controls) down. A 3+ line title overflows this
      // box and lets the centered group shift — which is acceptable.
      minHeight: TITLE_LINE_HEIGHT * 2 * scale,
      justifyContent: 'flex-end',
    },
    infoTitle: {
      fontSize: 44 * scale,
      lineHeight: TITLE_LINE_HEIGHT * scale,
      fontWeight: '700',
      color: '#fff',
      textAlign: 'center',
    },
    infoSubtitle: {
      fontSize: 24 * scale,
      fontWeight: '600',
      color: 'rgba(255, 255, 255, 0.7)',
      textAlign: 'center',
    },
    infoFooter: {
      gap: 18 * scale,
    },
    progressSection: {
      gap: 10 * scale,
    },
    timeLabel: {
      fontSize: 26 * scale,
      fontWeight: '700',
      color: 'rgba(255, 255, 255, 0.9)',
      textAlign: 'center',
    },
    progressBarContainer: {
      width: '100%',
    },
    controls: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 16 * scale,
      marginTop: 4 * scale,
    },
    controlButton: {
      width: 48 * scale,
      height: 48 * scale,
      borderRadius: 24 * scale,
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      borderWidth: 2,
      borderColor: 'rgba(255, 255, 255, 0.3)',
      alignItems: 'center',
      justifyContent: 'center',
    },
    controlButtonLarge: {
      width: 56 * scale,
      height: 56 * scale,
      borderRadius: 28 * scale,
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
    },
    controlButtonFocused: {
      borderColor: '#fff',
      backgroundColor: 'rgba(255, 255, 255, 0.35)',
    },
    backButton: {
      position: 'absolute',
      top: 24 * scale,
      left: 24 * scale,
      zIndex: 10,
      paddingVertical: 8 * scale,
      paddingHorizontal: 20 * scale,
      backgroundColor: 'rgba(255,255,255,0.15)',
      borderRadius: 20 * scale,
      borderWidth: 2,
      borderColor: 'transparent',
    },
    backButtonFocused: {
      backgroundColor: 'rgba(255,255,255,0.3)',
      borderColor: '#fff',
    },
    backButtonText: {
      color: '#fff',
      fontSize: 14 * scale,
      fontWeight: '600',
    },
  });
}
